import { WebGLContext } from 'features/assetDatabase/contexts/WebGLLayer'
import { ForecastAssetViz } from 'features/assetVizAdapter'
import React, { useContext, useEffect } from 'react'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
} from 'types'

export interface ManagedAssetVizProps {
  id: string
  assetModel: GeckoAssetModel | undefined
  components: readonly GeckoComponent[] | undefined
  getColorThresholdForComponent?: (
    cdf: GeckoComponentDataFrame
  ) => GeckoColorScaleThreshold | undefined
  componentDataFrame: GeckoComponentDataFrame | undefined
  assetColor: string
  setAssetImage?: React.Dispatch<React.SetStateAction<string | null>>
}

const NUMBER_OF_ASSET_VIZ = 8

export const ManagedAssetViz: React.FC<ManagedAssetVizProps> = ({
  id,
  assetModel,
  components,
  getColorThresholdForComponent,
  componentDataFrame,
  assetColor,
  setAssetImage,
}) => {
  const { setAssetVizRenderingMap, assetVizRenderingMap } =
    useContext(WebGLContext)

  // Functional updates: several instances mount and unmount in the same commit,
  // and a stale map copy would resurrect or drop their entries.
  useEffect(() => {
    setAssetVizRenderingMap((previous) => {
      if (previous.size > NUMBER_OF_ASSET_VIZ || previous.has(id)) {
        return previous
      }
      const updatedMap = new Map(previous)
      updatedMap.set(
        id,
        <ForecastAssetViz
          id={id}
          assetColor={assetColor}
          assetModel={assetModel}
          getColorThresholdForComponent={getColorThresholdForComponent}
          components={components}
          componentDataFrames={componentDataFrame ? [componentDataFrame] : []}
          setAssetImage={setAssetImage}
          offscreenRender={true}
        />
      )
      return updatedMap
    })
  }, [
    assetColor,
    assetModel,
    assetVizRenderingMap,
    getColorThresholdForComponent,
    componentDataFrame,
    components,
    id,
    setAssetImage,
    setAssetVizRenderingMap,
  ])

  // Free the render slot if this unmounts before its image is ready (e.g. a
  // list re-renders); otherwise the abandoned entry blocks later renders.
  useEffect(
    () => () =>
      setAssetVizRenderingMap((previous) => {
        if (!previous.has(id)) return previous
        const updatedMap = new Map(previous)
        updatedMap.delete(id)
        return updatedMap
      }),
    [id, setAssetVizRenderingMap]
  )

  return <>{assetVizRenderingMap.get(id)}</>
}
